/**
 * ETag / If-Match helpers for optimistic-concurrency edits.
 *
 * CLAUDE-A's http layer already does the wire work: `apiRequest` returns the
 * raw `ETag` response header on every `ApiResponse`, `ifMatch` sets the
 * `If-Match` request header, and a mismatched write throws
 * `PreconditionFailedError` (412). This module adds only the capture/carry
 * pattern edit screens repeat: fetch-with-etag, a ref to hold the etag between
 * load and save, and a typed 412 check.
 */
import { useRef, type RefObject } from "react";

import { ApiError, PreconditionFailedError } from "@/lib/api/errors";
import { apiRequestOperational as apiRequest, type ApiRequestOptions } from "@/lib/api/http";

export { PreconditionFailedError } from "@/lib/api/errors";

/**
 * True for a 412 optimistic-concurrency conflict (`If-Match` no longer
 * matches — the record changed elsewhere). Screens respond with the
 * "record changed elsewhere" refresh affordance, never a silent overwrite.
 * CLAUDE-A's `useFormSubmit` + `ConflictDialog` already implement that UX for
 * form saves; use this check for non-form mutations (row actions, dialogs).
 */
export function isPreconditionFailed(error: unknown): error is PreconditionFailedError {
  return (
    error instanceof PreconditionFailedError || (error instanceof ApiError && error.status === 412)
  );
}

export interface WithEtag<T> {
  data: T;
  /** Raw ETag header, ready for `If-Match`. Null when the server sent none. */
  etag: string | null;
}

/**
 * GET a record and capture its ETag for a later `If-Match` write.
 * Thin rename over `apiRequest` — use wherever a detail fetch feeds an edit.
 */
export async function fetchWithEtag<T>(
  path: string,
  opts: ApiRequestOptions = {},
): Promise<WithEtag<T>> {
  const { data, etag } = await apiRequest<T>(path, opts);
  return { data, etag };
}

/**
 * A ref (`{ current: string | null }`) that carries the captured ETag from
 * load to save without re-rendering. The edit-form pattern:
 *
 * ```ts
 * const etagRef = useEtagRef();
 *
 * // load (queryFn or effect): capture alongside the data
 * const { data, etag } = await fetchWithEtag<Thing>(`/things/${id}`);
 * etagRef.current = etag;
 *
 * // save: send If-Match; A's http layer throws PreconditionFailedError on 412
 * await apiFetch(`/things/${id}`, {
 *   method: "PUT",
 *   body,
 *   ifMatch: etagRef.current ?? undefined,
 * });
 *
 * // on 412 (isPreconditionFailed / useFormSubmit's `conflict`): show the
 * // ConflictDialog; its onReload refetches, re-captures the fresh etag into
 * // etagRef.current, and resets the form.
 * ```
 */
export function useEtagRef(): RefObject<string | null> {
  return useRef<string | null>(null);
}
